import { components } from "api/config/chalice-api";
import { v4 as uuidv4 } from "uuid";

// Import all necessary types
type DesignTemplate = components["schemas"]["DesignTemplate"];
type DesignPage = components["schemas"]["DesignPage"];
type ImageComponent = components["schemas"]["ImageComponent"];
type VideoComponent = components["schemas"]["VideoComponent"];
type DesignRenderType = components["schemas"]["DesignRenderType"];

/**
 * Represents basic dimensions.
 */
interface Dimensions {
  width: number;
  height: number;
}

/**
 * Represents video metadata including dimensions and duration.
 */
interface VideoMetadata extends Dimensions {
  duration?: number; // Duration in milliseconds
}

// Basic check for common image/video extensions
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogv|mov|avi|mkv)$/i; // Add more as needed

/**
 * Asynchronously retrieves the natural width and height of an image from its URL.
 * @param url - The URL of the image.
 * @returns A promise that resolves with the image dimensions { width, height }.
 * @throws If the image cannot be loaded or its dimensions cannot be determined.
 */
export const getImageDimensions = (url: string): Promise<Dimensions> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (error) => {
      console.error(`Failed to load image dimensions for URL: ${url}`, error);
      reject(new Error(`Could not load image dimensions for URL: ${url}`));
    };
    img.src = url;
  });
};

/**
 * Asynchronously retrieves the width, height, and duration of a video from its URL.
 * Loads only metadata, not the entire file.
 * @param url - The URL of the video.
 * @returns A promise that resolves with video metadata { width, height, duration? }.
 * @throws If the video metadata cannot be loaded.
 */
export const getVideoMetadata = (url: string): Promise<VideoMetadata> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    const onLoadedMetadata = () => {
      const durationMs = isFinite(video.duration)
        ? video.duration * 1000
        : undefined;
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: durationMs,
      });
      cleanup();
    };

    const onError = (e: Event | string) => {
      const errorEvent =
        e instanceof Event ? (e.target as HTMLVideoElement)?.error : e;
      console.error(
        `Failed to load video metadata for URL: ${url}`,
        errorEvent
      );
      reject(
        new Error(
          `Could not load video metadata for URL: ${url}. Error: ${errorEvent instanceof MediaError ? errorEvent.message : String(errorEvent)}`
        )
      );
      cleanup();
    };

    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("error", onError);
      video.src = ""; // Prevent further loading
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("error", onError);
    video.src = url;
  });
};

/**
 * Creates a standard ImageComponent object with defaults.
 * @param params - Required parameters for the image component.
 * @returns An ImageComponent object.
 */
const createImageComponent = ({
  src,
  width,
  height,
  x,
  y,
  name,
}: {
  src: string;
  width: number;
  height: number;
  x: number;
  y: number;
  name: string;
}): ImageComponent => {
  return {
    id: uuidv4(),
    type: "image",
    src,
    width,
    height,
    x,
    y,
    name,
    // --- Defaults ---
    rotation: 0,
    blurEnabled: false,
    blurRadius: 0,
    brightnessEnabled: false,
    brightness: 0,
    shadowEnabled: false,
    shadowBlur: 0,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowColor: "",
    shadowOpacity: 1,
    keepRatio: false,
    clipSrc: "",
    cropX: 0,
    cropY: 0,
    cropWidth: 1,
    cropHeight: 1,
    cornerRadius: 0,
    borderColor: "",
    borderSize: 0,
    flipX: false,
    flipY: false,
    selectable: true,
    alwaysOnTop: false,
    showInExport: true,
    draggable: true,
    contentEditable: true,
    removable: true,
    resizable: true,
    animations: [],
    grayscaleEnabled: false,
    opacity: 1,
    sepiaEnabled: false,
    styleEditable: true,
    visible: true,
  };
};

/**
 * Creates a standard VideoComponent object with defaults.
 * @param params - Required parameters for the video component.
 * @returns A VideoComponent object.
 */
const createVideoComponent = ({
  src,
  width,
  height,
  x,
  y,
  name,
  durationMs,
}: {
  src: string;
  width: number;
  height: number;
  x: number;
  y: number;
  name: string;
  durationMs?: number;
}): VideoComponent => {
  return {
    id: uuidv4(),
    type: "video",
    src,
    width,
    height,
    x,
    y,
    name,
    startTime: 0,
    endTime: durationMs ? durationMs / 1000 : 1, // Convert ms to seconds, default 1s
    // --- Defaults ---
    rotation: 0,
    shadowEnabled: false,
    shadowBlur: 5,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowColor: "black",
    shadowOpacity: 1,
    cropX: 0,
    cropY: 0,
    cropWidth: 1,
    cropHeight: 1,
    borderColor: "black",
    borderSize: 0,
    flipX: false,
    flipY: false,
    selectable: true,
    alwaysOnTop: false,
    showInExport: true,
    draggable: true,
    contentEditable: false, // Defaulting to false
    removable: false, // Defaulting to false
    resizable: false, // Defaulting to false
  };
};

/**
 * Creates a DesignTemplate object from a list of media URLs (images or videos).
 * Each URL corresponds to a page containing a single centered media component.
 * The template dimensions are based on the first successfully processed media item.
 *
 * @param mediaUrls - An array of image or video URLs.
 * @returns A Promise resolving to the generated DesignTemplate.
 * @throws If mediaUrls is empty or if metadata cannot be fetched for any URL.
 */
export const createDesignTemplateFromMediaUrls = async (
  mediaUrls: string[]
): Promise<DesignTemplate> => {
  if (!mediaUrls || mediaUrls.length === 0) {
    throw new Error("mediaUrls array cannot be empty.");
  }

  // Fetch metadata/dimensions concurrently
  const allMetadataPromises = mediaUrls.map((url) => {
    if (VIDEO_EXTENSIONS.test(url)) {
      return getVideoMetadata(url);
    } else if (IMAGE_EXTENSIONS.test(url)) {
      return getImageDimensions(url);
    } else {
      // Fallback: Try as image for unrecognized extensions
      console.warn(
        `Unrecognized extension for ${url}, attempting to load as image.`
      );
      return getImageDimensions(url);
    }
  });

  const allMetadataResults = await Promise.allSettled(allMetadataPromises);

  // Process results, logging errors and collecting successful metadata
  const validMetadata: (Dimensions | VideoMetadata)[] = [];
  const originalIndices: number[] = []; // Keep track of original index for URL lookup
  allMetadataResults.forEach((result, index) => {
    if (result.status === "fulfilled") {
      validMetadata.push(result.value);
      originalIndices.push(index); // Store the original index
    } else {
      console.error(
        `Failed to get metadata for ${mediaUrls[index]}: ${result.reason}`
      );
    }
  });

  if (validMetadata.length === 0) {
    throw new Error(
      "Could not determine metadata for any of the provided media URLs."
    );
  }

  // Use the dimensions from the *first* successfully processed item for the template
  const firstValidMetadata = validMetadata[0];
  const templateWidth = firstValidMetadata.width;
  const templateHeight = firstValidMetadata.height;

  let pageIndex = 0; // Keep track of successful pages created
  const pages: DesignPage[] = validMetadata.map(
    (metadata, validIndex): DesignPage => {
      // Iterate over successful results
      const urlIndex = originalIndices[validIndex]; // Get original URL index
      const url = mediaUrls[urlIndex];
      const isVideo = VIDEO_EXTENSIONS.test(url); // Re-check type based on original URL

      const mediaWidth = metadata.width;
      const mediaHeight = metadata.height;
      // Duration is only present in VideoMetadata
      const mediaDuration = (metadata as VideoMetadata).duration;

      const mediaX = (templateWidth - mediaWidth) / 2;
      const mediaY = (templateHeight - mediaHeight) / 2;

      let mediaComponent: ImageComponent | VideoComponent;

      if (isVideo) {
        mediaComponent = createVideoComponent({
          src: url,
          width: mediaWidth,
          height: mediaHeight,
          x: mediaX,
          y: mediaY,
          name: `Video ${pageIndex + 1}`,
          durationMs: mediaDuration,
        });
      } else {
        mediaComponent = createImageComponent({
          src: url,
          width: mediaWidth,
          height: mediaHeight,
          x: mediaX,
          y: mediaY,
          name: `Image ${pageIndex + 1}`,
        });
      }

      pageIndex++; // Increment successful page count

      return {
        id: uuidv4(),
        children: [mediaComponent],
        width: "auto",
        height: "auto",
        background: "white",
        bleed: 0,
        duration: mediaDuration ?? 5000, // ms
      };
    }
  );
  // No need to filter nulls as we iterate over successful results directly

  if (pages.length === 0) {
    // This condition might be redundant now if we check validMetadata.length > 0 earlier
    // but kept for safety. It means all promises settled but none were fulfilled.
    throw new Error(
      "Failed to create any pages due to metadata fetching errors for all URLs."
    );
  }

  const containsVideo = pages.some((p) =>
    p.children.some((c) => c.type === "video")
  );
  const finalRenderType: DesignRenderType = containsVideo ? "VIDEO" : "IMAGE";

  const designTemplate: DesignTemplate = {
    id: uuidv4(),
    pages: pages,
    width: templateWidth,
    height: templateHeight,
    render_design_as: finalRenderType,
    // --- Defaults ---
    theme: null,
    enabled: true,
    video_url: null,
    description: null,
    fonts: [],
    unit: "px",
    dpi: 72,
    audios: [],
    created_at: new Date().toISOString(),
  };

  return designTemplate;
};

/**
 * Adds a new page with a single centered media item (image or video) to an existing DesignTemplate.
 *
 * @param template - The existing DesignTemplate object.
 * @param mediaUrl - The URL of the image or video to add.
 * @returns A Promise resolving to the updated DesignTemplate. If media metadata
 *          cannot be fetched, it returns the original template and logs an error.
 */
export const addMediaUrlToDesignTemplate = async (
  template: DesignTemplate,
  mediaUrl: string
): Promise<DesignTemplate> => {
  let metadata: Dimensions | VideoMetadata | null = null;
  const isVideo = VIDEO_EXTENSIONS.test(mediaUrl);
  const isImage = IMAGE_EXTENSIONS.test(mediaUrl);

  try {
    if (isVideo) {
      metadata = await getVideoMetadata(mediaUrl);
    } else if (isImage) {
      metadata = await getImageDimensions(mediaUrl);
    } else {
      // Fallback for unrecognized types
      console.warn(
        `Unrecognized extension for ${mediaUrl} in addMediaUrlToDesignTemplate, attempting as image.`
      );
      metadata = await getImageDimensions(mediaUrl);
    }
  } catch (error) {
    console.error(
      `Failed to add media URL "${mediaUrl}" to template: Could not get metadata.`,
      error
    );
    return template; // Return original template on error
  }

  if (!metadata) {
    // Should be caught by try/catch, but safeguard
    console.error(
      `Failed to add media URL "${mediaUrl}" to template: Metadata was null.`
    );
    return template;
  }

  const templateWidth = template.width;
  const templateHeight = template.height;
  const mediaWidth = metadata.width;
  const mediaHeight = metadata.height;
  const mediaDuration = (metadata as VideoMetadata).duration; // Will be undefined for images

  // --- Calculate scaling to fit media within template dimensions --- //
  const templateRatio = templateWidth / templateHeight;
  const mediaRatio = mediaWidth / mediaHeight;

  let scale: number;
  if (mediaRatio > templateRatio) {
    // Media is wider than template aspect ratio, scale based on width
    scale = templateWidth / mediaWidth;
  } else {
    // Media is taller than or same aspect ratio as template, scale based on height
    scale = templateHeight / mediaHeight;
  }

  // Calculate scaled dimensions and position
  const scaledWidth = mediaWidth * scale;
  const scaledHeight = mediaHeight * scale;
  const scaledX = (templateWidth - scaledWidth) / 2;
  const scaledY = (templateHeight - scaledHeight) / 2;

  let mediaComponent: ImageComponent | VideoComponent;
  if (isVideo) {
    mediaComponent = createVideoComponent({
      src: mediaUrl,
      width: scaledWidth,
      height: scaledHeight,
      x: scaledX,
      y: scaledY,
      name: `Video ${template.pages.length + 1}`,
      durationMs: mediaDuration,
    });
  } else {
    mediaComponent = createImageComponent({
      src: mediaUrl,
      width: scaledWidth,
      height: scaledHeight,
      x: scaledX,
      y: scaledY,
      name: `Image ${template.pages.length + 1}`,
    });
  }

  const newPage: DesignPage = {
    id: uuidv4(),
    children: [mediaComponent],
    width: "auto",
    height: "auto",
    background: "white",
    bleed: 0,
    duration: mediaDuration ?? 5000, // ms
  };

  const newRenderType: DesignRenderType =
    isVideo || template.render_design_as === "VIDEO" ? "VIDEO" : "IMAGE";

  return {
    ...template,
    pages: [...template.pages, newPage],
    render_design_as: newRenderType,
  };
};
